import { FeatureAccessFlags } from "services/subscriptions/model";

export type PricingModel = {
  id: string;
  name: string;
  code: string;
  description: string;
  target_audience?: string;
  benefits?: string;
  url: string;
  currency: string;
  type: PurchaseStudentType;
  items: PricingModelItem[];
  is_selected: boolean;
};

export type PurchaseStudentType = "one-on-one" | "self-study";
export type PurchaseType = "one-time" | "subscription";

export type PricingModelTeam = {
  id: string;
  name: string;
  company: {
    id: string;
    name: string;
  } | null;
};

export type PricingModelStudent = {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  created: string;
};

export type PricingModelItem = {
  id: string;
  name: string;
  order_number: number;
  price: number;
  ai_tokens: number | null;
  currency: string;
  purchase_student_type: PurchaseStudentType;
  purchase_type: PurchaseType;
  number: number | null;
  length: number | null;
  is_trial: boolean;
  included_content: FeatureAccessFlags;
};

// Subscription lengths are stored as day counts.
export const DAYS_PER_MONTH = 30;
export const DAYS_PER_YEAR = 365;

export const RENEWAL_PERIODS = ["monthly", "annual"] as const;
export type RenewalPeriod = (typeof RENEWAL_PERIODS)[number];

export function isRenewalPeriod(value: string): value is RenewalPeriod {
  return (RENEWAL_PERIODS as readonly string[]).includes(value);
}

// A monthly plan is stored as 30 or 31 days.
export function renewalPeriodOfDays(length: number | null): RenewalPeriod | null {
  if (length === DAYS_PER_MONTH || length === DAYS_PER_MONTH + 1) {
    return "monthly";
  }
  if (length === DAYS_PER_YEAR) {
    return "annual";
  }
  return null;
}

export const isPricingModelItemMonthly = (item: PricingModelItem) =>
  item.purchase_type === "subscription" && renewalPeriodOfDays(item.length) === "monthly";

// A buyer's account stays on the model it bought on, and a trial is closed to
// anyone who has paid once: a model with nothing else leaves them an empty
// purchase screen.
export const sellsOnlyTrials = (model: { items: PricingModelItem[] }) =>
  model.items.length > 0 && model.items.every(item => item.is_trial);
